import * as THREE from 'three';
import type { Disturbance } from '../physics/world';
import type { Military, Patrol, Soldier } from './military';
import type { Vec } from '../destruction/structure';

interface Alert {
  id: string;
  point: THREE.Vector3;
  radius: number;
  until: number;
  assigned: number;
}
interface Response {
  patrol: Patrol;
  alert: Alert;
  state: 'travelling' | 'unloading' | 'working' | 'returning';
  target: THREE.Vector3;
  elapsed: number;
  retry: number;
  progress: { distance: number; at: number };
  reported: Set<string>;
  reason: string;
}
const healthy = (s: Soldier) =>
  !s.person.fatal && !['incapacitated', 'falling', 'carried', 'recovering'].includes(s.person.state);

/** Non-combat aid from the existing patrol roster. A blast sends real occupied
 * vehicles to a safe roadside position; crews walk, inspect and direct nearby
 * civilians away from the incident, then board their original transport. */
export class MilitaryRelief {
  onDispatch: (text: string) => void = () => {};
  readonly alerts = new Map<string, Alert>();
  readonly responses = new Map<string, Response>();
  dispatches = 0;
  arrivals = 0;
  footFallbacks = 0;
  work = 0;
  private serial = 0;
  private scanAt = 0;
  constructor(readonly army: Military) {}
  clear(): void {
    for (const r of this.responses.values()) {
      r.patrol.car.incident = undefined;
      for (const s of r.patrol.crew) this.army.population.services?.assistance.release(s.person);
      for (const s of r.patrol.crew)
        if (s.task.startsWith('relief:')) {
          this.army.population.civil?.cancelTask(s.person, 'Military relief assignment ended');
          s.task = '';
        }
    }
    this.alerts.clear();
    this.responses.clear();
    this.serial = this.dispatches = this.arrivals = this.footFallbacks = this.work = this.scanAt = 0;
  }
  report(event: Disturbance): void {
    if (this.army.mobilised || event.kind === 'impact') return;
    const existing = [...this.alerts.values()].find(
      (a) => Math.hypot(a.point.x - event.point.x, a.point.z - event.point.z) < 80,
    );
    if (existing) {
      existing.until = this.army.time + 150;
      existing.radius = Math.max(existing.radius, Math.min(50, event.radius));
      return;
    }
    if (this.alerts.size >= 8) return;
    const id = `military-relief:${++this.serial}`;
    this.alerts.set(id, {
      id,
      point: new THREE.Vector3(event.point.x, event.point.y, event.point.z),
      radius: Math.min(50, event.radius),
      until: this.army.time + 150,
      assigned: 0,
    });
    this.scanAt = Math.min(this.scanAt, this.army.time);
  }
  private route(patrol: Patrol, alert: Alert): THREE.Vector3 | undefined {
    const { population, physics } = this.army,
      car = patrol.car,
      network = population.network;
    for (const approach of network.approaches(alert.point, true, car.length, 6)) {
      const e = approach.edge,
        a = network.nodes.get(e.from)!,
        b = network.nodes.get(e.to)!;
      const length = Math.hypot(b.x - a.x, b.z - a.z),
        dx = (b.x - a.x) / length,
        dz = (b.z - a.z) / length;
      for (const along of [0, 18, -18, 36, -36])
        for (const side of [1, -1]) {
          const road = new THREE.Vector3(
            approach.point.x,
            approach.point.y,
            approach.point.z,
          ).addScaledVector(new THREE.Vector3(dx, 0, dz), along);
          const progress = (road.x - a.x) * dx + (road.z - a.z) * dz;
          if (progress < car.length + 8 || progress > length - car.length - 8) continue;
          const target = new THREE.Vector3(a.x + dx * progress, road.y, a.z + dz * progress).addScaledVector(
            new THREE.Vector3(-dz, 0, dx),
            side * (e.width / 2 + (e.pavementWidth ?? 1.5) + car.width / 2 + 0.7),
          );
          if (
            Math.hypot(target.x - alert.point.x, target.z - alert.point.z) < Math.max(14, alert.radius + 5) ||
            population.hazardAt(target)
          )
            continue;
          if (
            [...this.responses.values()].some((r) => r.patrol !== patrol && r.target.distanceTo(target) < 12)
          )
            continue;
          const ground = physics.ground(target, 0.5, 0.8);
          if (!ground) continue;
          target.y = ground.y;
          const yaw = Math.atan2(-dx, -dz),
            q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
          if (
            !physics.occupiedBoxClear(
              { x: target.x, y: target.y + 1.1, z: target.z },
              { x: car.width / 2 + 0.2, y: 1, z: car.length / 2 + 0.3 },
              q,
              { x: 0, y: 0, z: 0 },
              car.body,
            )
          )
            continue;
          const plan = population.plannedRoute(car, { ...road, edge: e.id }, approach.node, true);
          if (!plan.length) continue;
          plan.push({ x: target.x, y: target.y, z: target.z, maxSpeed: 2 });
          car.plan = plan;
          car.parkingYaw = yaw;
          car.incident = alert.id;
          car.state = 'enroute';
          car.speed = 16;
          car.think = 0;
          patrol.destination = 'Civilian relief near reported damage';
          patrol.phase = 'response';
          return target;
        }
    }
  }
  private beginReturn(r: Response): void {
    r.state = 'returning';
    r.reason = 'Original relief crew returning to their patrol';
    for (const s of r.patrol.crew.slice(1))
      if (healthy(s) && s.state !== 'aboard') {
        this.army.population.services?.assistance.release(s.person);
        this.army.population.civil?.cancelTask(
          s.person,
          'Relief inspection complete; returning to original vehicle',
        );
        s.task = '';
        s.state = 'returning';
        s.retry = 0;
      }
  }
  private workPoint(s: Soldier, r: Response): Vec | undefined {
    const { population, physics } = this.army,
      network = population.civil!.network;
    return network
      .near(r.target, 24, 'street')
      .filter(
        (n) =>
          !population.hazardAt(n) &&
          Math.hypot(n.x - r.alert.point.x, n.z - r.alert.point.z) > r.alert.radius + 3 &&
          Math.hypot(n.x - r.patrol.car.p.x, n.z - r.patrol.car.p.z) > r.patrol.car.width / 2 + 1 &&
          physics.ground(n, 0.35, 0.5) &&
          !physics.blocked(n.x, n.z, 0.27, n.y + 0.85),
      )
      .sort(
        (a, b) =>
          Math.hypot(a.x - r.alert.point.x, a.z - r.alert.point.z) -
          Math.hypot(b.x - r.alert.point.x, b.z - r.alert.point.z),
      )[(s.seat ?? 1) - 1];
  }
  update(dt: number): void {
    const { population, time } = this.army,
      civil = population.civil;
    if (this.army.mobilised || !civil) return;
    if (time >= this.scanAt) {
      this.scanAt = time + 0.5;
      for (const alert of this.alerts.values()) {
        if (time > alert.until) {
          if (![...this.responses.values()].some((r) => r.alert === alert)) this.alerts.delete(alert.id);
          continue;
        }
        if (alert.assigned >= 6) continue;
        const candidates = this.army.patrols
          .filter(
            (p) =>
              ['patrol', 'braking', 'inspection'].includes(p.phase) &&
              p.car.state !== 'wreck' &&
              healthy(p.crew[0]) &&
              !this.responses.has(p.car.id),
          )
          .sort((a, b) => a.car.p.distanceToSquared(alert.point) - b.car.p.distanceToSquared(alert.point));
        for (const patrol of candidates.slice(0, 4)) {
          const alreadyOutside = patrol.crew.some((s) => healthy(s) && s.state !== 'aboard');
          let target = alreadyOutside ? undefined : this.route(patrol, alert);
          const onFoot = !target && patrol.car.p.distanceTo(alert.point) < 220;
          if (onFoot) {
            target = patrol.car.p.clone();
            patrol.phase = 'response';
            patrol.car.incident = alert.id;
            patrol.car.plan = [];
            patrol.car.speed = patrol.car.targetSpeed = 0;
            patrol.car.state = 'parked';
            this.footFallbacks++;
          }
          if (!target) continue;
          this.responses.set(patrol.car.id, {
            patrol,
            alert,
            state: onFoot ? 'unloading' : 'travelling',
            target,
            elapsed: 0,
            retry: time + 5,
            progress: { distance: patrol.car.p.distanceTo(target), at: time },
            reported: new Set(),
            reason: onFoot
              ? 'Road route closed; original crew disembarking on the reachable side'
              : 'Responding from original patrol position to nearby destruction',
          });
          alert.assigned++;
          this.dispatches++;
          if (alert.assigned === 1) this.onDispatch('Garrison patrols responding to nearby damage');
          break;
        }
      }
    }
    for (const r of this.responses.values()) {
      const p = r.patrol,
        car = p.car;
      if (car.state === 'wreck' || !healthy(p.crew[0])) {
        this.responses.delete(car.id);
        continue;
      }
      r.elapsed += dt;
      if (r.state === 'travelling') {
        // Rocking/reversing at the same wreck is not progress to the goal.
        const distance = car.p.distanceTo(r.target);
        if (distance < r.progress.distance - 1.5) r.progress = { distance, at: time };
        const stalled = time - r.progress.at > 4.5;
        car.speed = 16 * (0.45 + 0.55 * car.health);
        const arrived = car.p.distanceTo(r.target) < 2 && car.actualSpeed < 0.1;
        const footFallback = stalled && car.actualSpeed < 1 && car.p.distanceTo(r.alert.point) < 220;
        if (arrived || footFallback) {
          car.plan = [];
          car.speed = 0;
          car.targetSpeed = 0;
          car.state = 'parked';
          if (footFallback) r.target.copy(car.p);
          r.state = 'unloading';
          r.elapsed = 0;
          r.reason = footFallback
            ? 'Blocked vehicle stopped; original crew continuing on foot'
            : 'Original patrol reached safe roadside staging';
          if (arrived) this.arrivals++;
          else this.footFallbacks++;
        } else if (stalled && time >= r.retry) {
          r.retry = time + 8;
          const target = this.route(p, r.alert);
          if (target) {
            r.target = target;
            r.progress = { distance: car.p.distanceTo(target), at: time };
          }
        }
      }
      if (r.state === 'unloading' || r.state === 'working') {
        car.speed = 0;
        for (const s of p.crew.slice(1)) {
          if (!healthy(s)) continue;
          if (s.state === 'aboard' && r.elapsed > (s.seat ?? 1) * 0.55) this.army.beginExit(s);
          if (s.transition || s.state === 'aboard') continue;
          r.state = 'working';
          if (population.services?.assistance.step(s.person, r.alert.point, dt)) {
            s.task = `relief:${r.alert.id}:${s.id}:assistance`;
            r.reason = 'Original crew using supported access for first aid and loose-rubble clearance';
            continue;
          }
          const task = civil.task(s.person);
          if (task?.token === s.task && task.state === 'completed') {
            if (!r.reported.has(s.id)) {
              r.reported.add(s.id);
              this.work++;
              s.checks++;
              p.checks++;
              s.person.state = 'directing';
              r.reason = 'Grounded crew inspecting damage and directing civilians away';
              for (const incident of population.services?.incidents.values() ?? [])
                if (incident.point.distanceTo(r.alert.point) < 100)
                  population.services!.observeIncident(incident.id);
              population.actorsNear(r.alert.point, r.alert.radius + 18, (actor) => {
                if (
                  actor.category === 'person' &&
                  !actor.faction &&
                  !actor.controlled &&
                  !['incapacitated', 'falling', 'carried', 'recovering'].includes(actor.state)
                )
                  population.evacuate(actor, r.alert.point);
              });
            }
          } else if (!r.reported.has(s.id) && (!task || task.state !== 'active') && time >= s.retry) {
            s.retry = time + 3;
            const point = this.workPoint(s, r);
            if (point) {
              const node = civil.network.nodes[civil.network.nearest(point, 'street')];
              const token = `relief:${r.alert.id}:${s.id}:${++s.sequence}`;
              if (
                civil.assignTask(s.person, civil.nodePlace(node), token, 'inspecting incident perimeter', 3)
              )
                s.task = token;
            }
          }
        }
        if (time > r.alert.until && r.elapsed > 20) this.beginReturn(r);
      }
      if (r.state === 'returning') {
        for (const s of p.crew) if (healthy(s) && s.state !== 'aboard') this.army.returnToVehicle(s, dt);
        if (p.crew.every((s) => !healthy(s) || s.state === 'aboard')) {
          car.incident = undefined;
          car.speed = 8;
          car.state = 'parked';
          p.phase = 'patrol';
          this.responses.delete(car.id);
        }
      }
    }
  }
  snapshot(): Record<string, number> {
    return {
      militaryReliefDispatches: this.dispatches,
      militaryReliefArrivals: this.arrivals,
      militaryReliefFootFallbacks: this.footFallbacks,
      militaryReliefWork: this.work,
    };
  }
  inspect(): unknown {
    return {
      dispatches: this.dispatches,
      arrivals: this.arrivals,
      footFallbacks: this.footFallbacks,
      work: this.work,
      alerts: [...this.alerts.values()].map((a) => ({ ...a, point: a.point.toArray() })),
      units: [...this.responses.values()].map((r) => ({
        id: r.patrol.car.id,
        state: r.state,
        target: r.target.toArray(),
        reason: r.reason,
        completed: [...r.reported],
      })),
    };
  }
}
